/* eslint-disable */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { getColumnFilterType } from '@/utils/columnConfigUtils'

export const useCustomDataTable = ({
  columns = [],
  records = [],
  pageSize = 10,
  onResetAll = () => { },
  onRowSelect = () => { },
  handleSorting = () => { },
  setSearchArry = () => { },
  setPageIndex = () => { },
  isSelectAll = false,
  totalRecords = 0,
  pageIndex = 0,
  searchArr = [],
}) => {
  /** @type {object} State for date range filters */
  const [dateRanges, setDateRanges] = useState({})
  /** @type {object} State for single date filters */
  const [singleDates, setSingleDates] = useState({})
  /** @type {string} Which date picker is open */
  const [openDatePicker, setOpenDatePicker] = useState('')
  /** @type {array} Currently selected rows */
  const [selectedRows, setSelectedRows] = useState([])

  /** @type {object} Sort config (key, direction) */
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'desc',
  })
  /** @type {object} Filter values by column */
  const [filter, setFilter] = useState(
    columns.reduce(
      (acc, column) => ({
        ...acc,
        [column.name_of_db_column]: column.defaultFilterValue || '',
      }),
      {}
    )
  )
  /** @type {boolean} Track global select all */
  const [globalSelectAll, setGlobalSelectAll] = useState(false)
  /** @type {NodeJS.Timeout|null} Debounce ref for search */
  const debounceRef = useRef(null)

  useEffect(() => {
    if (isSelectAll) {
      setGlobalSelectAll(true)
      // When isSelectAll is true, replace all selections with current page
      setSelectedRows(records)
      onRowSelect(records, true)
    } else if (!isSelectAll && globalSelectAll) {
      setGlobalSelectAll(false)
      setSelectedRows([])
      onRowSelect([], false)
    }
  }, [isSelectAll, records])

  /**
   * Reset all filters, sorting, and pagination
   */
  const handleResetAll = useCallback(() => {
    // Call parent reset if provided (for external state)
    onResetAll();
    // Clear all local filter states
    setDateRanges({});
    setSingleDates({});
    setOpenDatePicker('');
    setSelectedRows([]);
    onRowSelect([], false);
    setFilter(
      columns.reduce(
        (acc, column) => ({
          ...acc,
          [column.name_of_db_column]: column.defaultFilterValue || '',
        }),
        {}
      )
    );
    setSortConfig({ key: null, direction: 'asc' }); 
    // If you have a local page state, reset it here (else rely on setPageIndex from props)
    // setPageIndex(0); // Uncomment if local page state is used

    setSingleDates({});
    setOpenDatePicker('');
    setSelectedRows([]);
    onRowSelect([], false);
    setFilter(
      columns.reduce(
        (acc, column) => ({
          ...acc,
          [column.name_of_db_column]: column.defaultFilterValue || '',
        }),
        {}
      )
    );
  }, [onResetAll, columns, onRowSelect]);

  /**
   * Memoized total pages for pagination
   */
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalRecords / pageSize)), [totalRecords, pageSize])

  /**
   * Handle sorting by column
   * @param {string} key
   */
  const handleSort = useCallback(
    key => {
      let newDirection

      // If clicking a new column, start with desc
      if (sortConfig.key !== key) {
        newDirection = 'desc'
      }
      // If clicking the same column, toggle between desc and asc
      else {
        newDirection = sortConfig.direction === 'desc' ? 'asc' : 'desc'
      }

      setSortConfig({ key, direction: newDirection })
      handleSorting({ column: key, direction: newDirection })
    },
    [sortConfig, handleSorting]
  )

  /**
   * Handle select/deselect all rows
   */
  const handleSelectAll = useCallback(() => {
    const allCurrentPageSelected = records.every(record => {
      const recordId = record.productId ?? record.orderId ?? record.item
      return recordId && selectedRows.some(selected => (selected.productId ?? selected.orderId ?? selected.item) === recordId)
    })

    if (allCurrentPageSelected) {
      // Clear all selections when deselecting
      setSelectedRows([])
      setGlobalSelectAll(false)
      onRowSelect([], false)
    } else {
      // Replace all selections with current page when selecting all
      setSelectedRows(records)
      setGlobalSelectAll(true)
      onRowSelect(records, true)
    }
  }, [records, selectedRows, onRowSelect])

  /**
   * Handle single row selection toggle
   * @param {object} rowObj
   */
  const handleRowSelect = useCallback( 
    rowObj => {
      if (!rowObj) return
      const identifier = rowObj.productId ?? rowObj.orderId ?? rowObj.item
      if (!identifier) return

      const isSelected = selectedRows.some(row => {
        const rowId = row.productId ?? row.orderId ?? row.item
        return rowId && rowId === identifier
      })

      let newSelection
      if (isSelected) {
        newSelection = selectedRows.filter(row => {
          const rowId = row.productId ?? row.orderId ?? row.item
          return rowId !== identifier
        })
        setGlobalSelectAll(false)
      } else {
        newSelection = [...selectedRows, rowObj]
      }

      setSelectedRows(newSelection)
      onRowSelect(newSelection, false)
      console.log(rowObj)
    },
    [selectedRows, onRowSelect]
  )

  // Handle page change - triggers new server request
  /**
   * Handle page change
   * @param {object} event
   * @param {number} value
   */

  /**
   * Handle filter text input (debounced)
   * @param {object} e
   */
  const handleFilterChange = useCallback(
    e => {
      if (!e?.target) return
      const { name, value } = e.target

      if (!name) return

      setFilter(prev => ({ ...prev, [name]: value ?? '' }))

      // Debounce search API call
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        const searchObj = {
          field: name,
          value: value ?? '',
          type: 'text',
        }
        setSearchArry(searchObj)
      }, 350)

      // Reset page to 1 when filtering
      setPageIndex(0)
    },
    [setSearchArry, setPageIndex]
  )

  /**
   * Handle date range filter (debounced)
   * @param {array} dates
   * @param {string} dateField
   */
  const handleDateRangeChange = useCallback(
    (dates, dateField) => {
      if (!dateField) return
      const [start, end] = dates || [null, null]

      // Always update the local date range state for UI
      setDateRanges(prev => ({
        ...prev,
        [dateField]: { startDate: start, endDate: end },
      }))

      // Only proceed with API call if both dates are selected
      if (start && end) {
        // Create dates at midnight UTC to avoid timezone issues
        const startUTC = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0))

        const endUTC = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999))

        // Debounce API call for date range
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
          const searchObj = {
            field: dateField,
            value: {
              from: startUTC.toISOString(),
              to: endUTC.toISOString(),
            },
            type: 'dateRange',
          }
          setSearchArry(searchObj)
          setPageIndex(0)
        }, 350)
      } else if (start === null && end === null) {
        const searchObj = {
          field: dateField,
          value: {
            from: '',
            to: '',
          },
          type: 'dateRange',
        }
        // Call API with the search object
        setSearchArry(searchObj)
        setPageIndex(0)
      }
      // Don't call setSearchArry if not both dates are present
    },
    [setSearchArry]
  )

  /**
   * Handle single date filter (debounced)
   * @param {Date} date
   * @param {string} dateField
   */
  const handleSingleDateChange = useCallback(
    (date, dateField) => {
      if (!dateField) return

      // Update the local state for UI
      setSingleDates(prev => ({
        ...prev,
        [dateField]: date,
      }))

      let formattedDate = ''
      if (date) {
        // Create date at midnight UTC to avoid timezone issues
        const dateUTC = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0))
        formattedDate = dateUTC.toISOString()
      }

      const searchObj = {
        field: dateField,
        value: formattedDate || '',
        type: 'date',
      }

      // Debounce API call for single date
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        setSearchArry(searchObj)
        setPageIndex(0)
      }, 350)
    },
    [setSearchArry]
  )

  // Initial data load - removed setSearchArry([]) to allow default values to be set

  // Sync filter state with search array for dropdown filters
  useEffect(() => {
    if (searchArr && searchArr.length > 0) {
      const updatedFilter = { ...filter };
      searchArr.forEach(searchItem => {
        if (searchItem.field && searchItem.value) {
          updatedFilter[searchItem.field] = searchItem.value;
        }
      });
      setFilter(updatedFilter);
    }
  }, [searchArr]);

  /**
   * Reset row selections
   */
  const resetSelection = useCallback(() => {
    setSelectedRows([])
    setGlobalSelectAll(false)
    onRowSelect([], false)
  }, [onRowSelect])

  return {
    dateRanges,
    singleDates,
    openDatePicker,
    selectedRows,
    sortConfig,
    filter,
    totalPages,
    setOpenDatePicker,
    handleResetAll,
    handleSort,
    handleSelectAll,
    handleRowSelect,
    handleFilterChange,
    handleDateRangeChange,
    handleSingleDateChange,
    resetSelection,
    // No page or handlePageChange returned
  }
}
